import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { StripeCardElementChangeEvent, TokenResult } from "@stripe/stripe-js/types/stripe-js";
import { flash } from "@xola/ui-kit";
import clsx from "clsx";
import { noop } from "lodash";
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { StripeBillingDetails, Traveler } from "types";
import { useSeller } from "../../hooks/useAccount";
import { hasCustomPaymentGateway, isAdditionalBillingInfoRequired } from "../../models/Seller";
import { PaymentFailedAlertModal } from "../PurchaseCreate/PaymentFailedAlertModal";
import { StripeAdditionalBillingDetailsModal } from "../PurchaseModification/StripeAdditionalBillingDetailsModal";
import { PaymentInputRef, PaymentInputRefParams } from "./PaymentInputRef";
import { api } from "@/api/api";
import { BillingInfoError } from "@/errors/requireBillingInfoError";
import { getCompatibleCardType } from "@/helpers/rawCardHelper";
import { useCreatePurchaseStore } from "@/stores/createPurchaseStore";

interface StripeCardInputProps {
    className?: string;
    organizer?: Traveler;
    onAdditionalDetailsSubmitSuccess?: () => void;
    onError: (error: string | null) => void;
}

export const StripeCardInput = forwardRef<PaymentInputRef, StripeCardInputProps>(
    ({ className, organizer, onAdditionalDetailsSubmitSuccess, onError }, ref) => {
        const seller = useSeller();
        const additionalBillInfoRequired = isAdditionalBillingInfoRequired(seller);
        const [processingError, setProcessingError] = useState<string | null>();
        const [paymentInputParams, setPaymentInputParams] = useState<PaymentInputRefParams>();
        const [isSubmitting, setIsSubmitting] = useState(false);
        const elements = useElements();
        const stripe = useStripe();

        const [additionalBillingDetailsState, setAdditionalBillingDetailsState] = useState<{
            isOpen: boolean;
            resolve: (shouldContinue: boolean) => void;
            reject: (error: Error) => void;
        }>({
            isOpen: false,
            resolve: noop,
            reject: noop,
        });

        // This hook allows us to expose an object to the parent via ref. The object can have
        // methods the parent can call. In this case, the parent needs to call a `confirmCard` method on the
        // child component (this one) to confirm the card payment when ready.
        useImperativeHandle(ref, () => ({
            async prepare() {
                return true;
            },

            async confirm(params?: PaymentInputRefParams) {
                setPaymentInputParams(params); // Save params in case the payment fails
                if (additionalBillInfoRequired) {
                    return new Promise<boolean>((resolve, reject) => {
                        setAdditionalBillingDetailsState({
                            isOpen: true,
                            resolve,
                            reject,
                        });
                    });
                }

                return confirmPayment(params);
            },

            async tokenize(): Promise<TokenResult | null> {
                const card = elements!.getElement(CardElement)!;
                const resp = await stripe?.createToken(card);
                return resp ?? null;
            },
        }));

        const handleCloseAdditionalBillingDetails = async (shouldContinue: boolean) => {
            if (shouldContinue) {
                setIsSubmitting(false);
                additionalBillingDetailsState.resolve(true);
            } else {
                const { purchaseId } = useCreatePurchaseStore.getState();
                if (purchaseId) {
                    try {
                        // Deleting the purchase here itself since user may cancel out of the billing info modal
                        await api.delete(`/purchases/${purchaseId}`);
                        additionalBillingDetailsState.reject(new BillingInfoError());
                    } catch (error: any) {
                        console.error("Error deleting purchase", purchaseId);
                        additionalBillingDetailsState.reject(error);
                    }
                }

                setIsSubmitting(false);
            }

            setAdditionalBillingDetailsState({ isOpen: false, resolve: noop, reject: noop });
        };

        const confirmPayment = async (params?: PaymentInputRefParams, billingDetails?: StripeBillingDetails) => {
            setIsSubmitting(true);
            const card = elements!.getElement(CardElement)!;

            if (!params?.clientSecret) {
                throw new Error(
                    "Parameter `clientSecret` is missing. Check the `confirm` method on `PaymentInput` ref.",
                );
            }

            const response = await stripe!.confirmCardPayment(params?.clientSecret, {
                payment_method: { card, billing_details: billingDetails },
            });

            if (response.error) {
                if (billingDetails) {
                    // We have billing details at this point, it must be something wrong with the card
                    setProcessingError(response.error.message);
                    throw response.error as unknown as Error;
                } else {
                    // Show billing details form, maybe that will fix the issue
                    setAdditionalBillingDetailsState({ isOpen: true, resolve: noop, reject: noop });
                    setIsSubmitting(false);
                    throw new BillingInfoError(response.error.message);
                }
            } else {
                setProcessingError(null);
                onError(null);
            }

            setIsSubmitting(false);
            return response;
        };

        useEffect(() => {
            // The input is visible, notify the parent that is required
            onError("is required");
        }, []); // eslint-disable-line react-hooks/exhaustive-deps

        const handleCardElementChange = (event: StripeCardElementChangeEvent) => {
            if (
                event.brand !== "unknown" &&
                hasCustomPaymentGateway(seller) &&
                !seller.acceptedCards.includes(getCompatibleCardType(event.brand.toLowerCase()))
            ) {
                onError("is not acceptable");
            } else if (event.complete) {
                onError(null);
            } else if (event.empty) {
                onError("is required");
            } else if (event.error) {
                onError(` - ${event.error.message}`);
            }
        };

        const handleSubmitBillingDetails = async (billingDetails: StripeBillingDetails) => {
            // Confirm payment again, but this time with additional billing details
            try {
                await confirmPayment(paymentInputParams, billingDetails);
                await handleCloseAdditionalBillingDetails(true);
                onAdditionalDetailsSubmitSuccess?.();
            } catch (error: any) {
                flash.show({ text: error.message, color: "danger" });
                await handleCloseAdditionalBillingDetails(false);
            }
        };

        const handleClose = async () => {
            // Disable the button since purchase will need to be rolled back
            setIsSubmitting(true);
            await handleCloseAdditionalBillingDetails(false);
        };

        return (
            <div
                className={clsx(
                    "w-full rounded border border-gray-light px-3 py-2.5 text-base text-gray-darker hover:border-black hover:bg-gray-lighter focus:text-black active:text-black",
                    className,
                )}
            >
                <CardElement
                    options={{ hidePostalCode: !seller.preferences.payment.enforcePostalCode }}
                    onChange={handleCardElementChange}
                />

                <StripeAdditionalBillingDetailsModal
                    isSubmitting={isSubmitting}
                    organizer={organizer}
                    isOpen={additionalBillingDetailsState.isOpen}
                    onSubmit={handleSubmitBillingDetails}
                    onClose={handleClose}
                />

                <PaymentFailedAlertModal
                    error={processingError}
                    onConfirm={() => {
                        setProcessingError(null);
                    }}
                />
            </div>
        );
    },
);
